import { JWMLink } from "@components/JWMLink/JWMLink";
import { Icon, jnjExternalLink } from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Image, ImageFitType } from "@jmc/solid-design-system/src/components/organisms/Image/Image";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { CMSImageProps } from "@jwm/types/CMSImageProps";
import { mdiLaunch } from "@mdi/js";
import classnames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "../style.module.scss";

interface Props {
    extension: string;
    imageLink: {
        url: string;
        url_prefix: string;
        external: boolean;
        title?: string;
        anchor_id?: string;
    };
    image: CMSImageProps;
    placement?: string;
    loading?: "lazy" | "eager";
}

export const JWMLinkedImage = (props: Props): JSX.Element => {
    const { extension = "jpg", imageLink, image, placement, loading = "lazy" } = props;
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const linkedImageIcon = (): JSX.Element => {
        return (
            <div className={style.image__link_icon_wrapper}>
                <div className={style.image__link_icon}>
                    <Icon color="text-light" icon={jnjFullBranded ? jnjExternalLink : mdiLaunch} size="medium" />
                </div>
            </div>
        );
    };

    const linkedImageCaption = (): JSX.Element => {
        return (
            <Typography
                variant="body"
                weight="400"
                size="s"
                className={classnames(
                    style.figcaption,
                    !image?.disable_lightbox_image_link_icon ? style.linkIcon : null,
                )}
            >
                {image?.caption}
            </Typography>
        );
    };

    const assetTitle = image?.image?.title;
    const newAssetTitle = assetTitle?.substring(0, assetTitle?.lastIndexOf("."));
    const imageFit = image?.fit?.toLowerCase() as ImageFitType;
    const isFitNone = imageFit === "none";
    const originalImgHeight = image?.image?.dimension?.height;
    const objectFit = isFitNone ? "cover" : imageFit;
    const styles = isFitNone
        ? {
              height: `${originalImgHeight}px`,
          }
        : { maxWidth: "100%", maxHeight: "100%" };
    const scaleDownImageStyle =
        imageFit === "scale-down"
            ? {
                  width: "100%",
                  maxWidth: `${image?.image?.dimension?.width}px`,
                  maxHeight: `${image?.image?.dimension?.height}px`,
              }
            : undefined;
    const imageTag =
        extension === "svg" || extension === "gif" ? (
            <Image
                url={image?.image?.url}
                enableLightBox={false}
                alt={image?.alt_text || image?.image?.description || ""}
                caption={image?.caption}
                title={image?.title || image?.alt_text}
                fit={imageFit}
                sizing={scaleDownImageStyle}
                loading={loading}
                enlargeLabel={t("Enlarge image", { ns: "common" })}
                horizontalAlignment={jnjFullBranded ? "start" : "center"}
            />
        ) : (
            <GatsbyImage
                image={image?.image?.gatsbyImageData}
                alt={image?.alt_text || image?.image?.description || ""}
                caption={image?.caption}
                title={image?.title || image?.alt_text}
                objectFit={objectFit}
                style={styles}
                loading={loading}
            />
        );

    return (
        <JWMLink
            name={imageLink?.title || image?.title || image?.image?.description || newAssetTitle}
            url={imageLink?.url}
            url_prefix={imageLink?.url_prefix}
            fullWidth={false}
            external={imageLink?.external}
            imageLink={true}
            placement={placement}
        >
            {imageTag}
            {!image?.disable_lightbox_image_link_icon && linkedImageIcon()}
            {jnjFullBranded && image?.caption && extension !== "svg" && extension !== "gif" && linkedImageCaption()}
        </JWMLink>
    );
};
export default JWMLinkedImage;
